import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Cpu,
  Network,
  Terminal,
  ShieldCheck,
} from "lucide-react";
import NeuralMonolith from "@/components/NeuralMonolith";
import PageShell, { SectionLabel } from "@/components/PageShell";
import SEO from "@/components/SEO";

const CAPABILITIES = [
  {
    icon: Cpu,
    title: "Applied AI R&D",
    desc: "Custom foundation models, evaluation suites and inference platforms — engineered for your data, not the benchmark.",
    span: "md:col-span-8",
  },
  {
    icon: Terminal,
    title: "Product Engineering",
    desc: "Full-stack teams that ship.",
    span: "md:col-span-4",
  },
  {
    icon: Network,
    title: "Distributed Systems",
    desc: "Multi-cloud, edge, embedded.",
    span: "md:col-span-4",
  },
  {
    icon: ShieldCheck,
    title: "Security & Cryptography",
    desc: "Post-quantum readiness, zero-trust architectures, and formally verified critical paths.",
    span: "md:col-span-8",
  },
];

const STACK = [
  "PyTorch",
  "JAX",
  "Triton",
  "CUDA",
  "Rust",
  "Go",
  "TypeScript",
  "Kubernetes",
  "Terraform",
  "gRPC",
  "PostgreSQL",
  "Redis",
  "React",
  "Next.js",
  "Three.js",
  "WebGPU",
  "Ray",
  "vLLM",
];

export default function Home() {
  return (
    <PageShell testId="page-home" className="pt-0 pb-0">
      <SEO
        title="Home — Imaginepedia Technologies"
        description="At Imaginepedia, we believe great technology starts with curiosity and relentless execution. We design, build, and scale software products across web, cloud, infrastructure, and emerging technologies. Whether it's powering digital experiences, creating intelligent platforms, or engineering robust systems, our focus remains the same: building technology that matters."
        path="/"
      />
      {/* HERO */}
      <section
        data-testid="home-hero"
        className="relative min-h-screen w-full overflow-hidden"
      >
        <div
          className="absolute inset-0 grid-lines opacity-40"
          aria-hidden="true"
        />
        <div className="absolute top-0 right-0 bottom-0 w-full md:w-[60%] z-0 pointer-events-none">
          <NeuralMonolith />
        </div>
        <div
          className="absolute top-0 left-0 bottom-0 w-full md:w-[45%] bg-gradient-to-r from-[#030303] via-[#030303]/85 to-transparent z-[1] pointer-events-none"
          aria-hidden="true"
        />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 min-h-screen flex flex-col justify-between pt-32 pb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="max-w-4xl"
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#CCFF00] mb-6 flex items-center gap-3">
              <span className="w-8 h-px bg-[#CCFF00]" />
              Imaginepedia · Software & AI R&amp;D
            </p>
            <h1
              data-testid="home-headline"
              className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-medium tracking-[-0.03em] leading-[0.95] text-white"
            >
              Turning
              <br />
              imaginations
              <br />
              into{" "}
              <span className="text-[#CCFF00]">
                reality<span className="blink text-[#CCFF00]">_</span>
              </span>
            </h1>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="grid grid-cols-1 md:grid-cols-12 items-end gap-8 mt-16"
          >
            <p className="md:col-span-6 text-lg text-zinc-400 leading-relaxed max-w-lg">
              We are a research-first studio building software and AI systems
              that ship. From frontier models to distributed platforms —
              engineered to run, not to demo.
            </p>
            <div className="md:col-span-6 flex flex-wrap gap-3 md:justify-end">
              <Link
                to="/contact"
                data-testid="hero-cta-primary"
                className="group inline-flex items-center gap-3 bg-[#CCFF00] text-black px-7 py-4 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-white transition-colors duration-200"
              >
                Start a project
                <ArrowUpRight
                  size={14}
                  className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-200"
                />
              </Link>
              <Link
                to="/about"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-3 border border-white/15 text-white px-7 py-4 font-mono text-[11px] uppercase tracking-[0.2em] hover:border-[#CCFF00]/50 hover:text-[#CCFF00] transition-colors duration-200"
              >
                Our approach
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Corner brackets */}
        <div className="absolute top-24 left-6 md:left-12 w-6 h-6 border-t border-l border-[#CCFF00]/60 z-10" />
        <div className="absolute top-24 right-6 md:right-12 w-6 h-6 border-t border-r border-[#CCFF00]/60 z-10" />
        <div className="absolute bottom-6 left-6 md:left-12 w-6 h-6 border-b border-l border-[#CCFF00]/60 z-10" />
        <div className="absolute bottom-6 right-6 md:right-12 w-6 h-6 border-b border-r border-[#CCFF00]/60 z-10" />
      </section>

      {/* STACK MARQUEE */}
      <section
        data-testid="home-marquee"
        className="border-y border-white/5 bg-[#050506] overflow-hidden"
      >
        <div className="flex whitespace-nowrap marquee-track py-6">
          {[...STACK, ...STACK].map((s, i) => (
            <span
              key={i}
              className="font-mono text-sm uppercase tracking-[0.3em] text-zinc-500 mx-8 flex items-center gap-8"
            >
              {s}
              <span className="w-1 h-1 bg-[#CCFF00]" />
            </span>
          ))}
        </div>
      </section>

      {/* CAPABILITIES BENTO */}
      <section
        data-testid="home-capabilities"
        className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32"
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16 items-end">
          <div className="md:col-span-8">
            <SectionLabel index={1}>Capabilities</SectionLabel>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-white leading-[1.02] tracking-tight">
              Research-grade rigor.
              <br />
              <span className="text-zinc-500">Product-grade delivery.</span>
            </h2>
          </div>
          <p className="md:col-span-4 text-zinc-400 leading-relaxed">
            Four practices. One team. We compose them per engagement so the
            shape of the work fits the shape of your problem.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          {CAPABILITIES.map((c, i) => (
            <motion.div
              key={c.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className={`${c.span} bg-[#0C0C0D] border border-white/5 p-8 md:p-10 hover:border-[#CCFF00]/30 transition-colors duration-300 group`}
            >
              <div className="flex items-start justify-between mb-12">
                <c.icon size={22} className="text-[#CCFF00]" />
                <span className="font-mono text-[10px] text-zinc-600">
                  0{i + 1}
                </span>
              </div>
              <h3 className="font-display text-2xl md:text-3xl text-white mb-3 group-hover:text-[#CCFF00] transition-colors duration-300">
                {c.title}
              </h3>
              <p className="text-zinc-400 leading-relaxed">{c.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* WHAT WE BUILD */}
      <section
        data-testid="home-manifesto"
        className="border-t border-white/5 bg-[#050506]"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-4">
            <SectionLabel index={2}>Manifesto</SectionLabel>
          </div>
          <div className="md:col-span-8">
            <p className="font-display text-3xl md:text-4xl lg:text-5xl text-white leading-[1.1] tracking-tight">
              We build the systems others describe in whitepapers.
              <br />
              <span className="text-zinc-500">
                Small teams. Deep ownership. Deployed in production, not slides.
              </span>
            </p>
            <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-8">
              {[
                { k: "4+", v: "Years cumulative research" },
                { k: "20+", v: "Systems in production" },
                // { k: "9", v: "Papers published" },
                { k: "24/7", v: "Global on-call" },
              ].map((s) => (
                <div key={s.v}>
                  <p className="font-display text-4xl md:text-5xl text-[#CCFF00] mb-1">
                    {s.k}
                  </p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                    {s.v}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="relative border border-white/10 p-10 md:p-20 overflow-hidden bg-[#0A0A0B]">
          <div
            className="absolute -top-20 -right-20 w-64 h-64 bg-[#CCFF00]/10 blur-3xl rounded-full"
            aria-hidden="true"
          />
          <div className="relative flex flex-col md:flex-row gap-8 justify-between items-start md:items-end">
            <h2 className="font-display text-3xl md:text-5xl text-white max-w-2xl leading-tight">
              Have an imagination that needs engineering?
            </h2>
            <Link
              to="/contact"
              data-testid="home-cta-final"
              className="group inline-flex items-center gap-3 bg-[#CCFF00] text-black px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-white transition-colors duration-200"
            >
              Let&apos;s talk
              <ArrowUpRight
                size={14}
                className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-200"
              />
            </Link>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
